import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Input,
  Separator,
  Skeleton,
  EmptyState,
  ErrorState,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vta/ui';
import { programLegend } from '../features/globe/programLegend.tsx';

/**
 * Dev-only token + primitives gallery. Mounted at `/tokens` (the `_dev`
 * segment is pathless). Hard-gated on `import.meta.env.DEV` so it cannot
 * leak into production bundles even if someone navigates by hand.
 */
export const Route = createFileRoute('/_dev/tokens')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) {
      throw redirect({ to: '/' });
    }
  },
  component: TokensReference,
});

function TokensReference() {
  return (
    <div className="mx-auto w-full max-w-6xl space-y-12 px-6 py-10 text-fg">
      <header className="space-y-2">
        <p className="text-2xs uppercase tracking-[0.3em] text-fg-subtle">VTA · design tokens</p>
        <h1 className="font-display text-4xl tracking-tight">Token reference</h1>
        <p className="max-w-prose text-sm text-fg-muted">
          Visual QA gate for DESIGN-001. Only mounted in development. Edit{' '}
          <code className="rounded-sm bg-bg-raised px-1 font-mono text-xs">
            apps/web/src/styles/tokens.css
          </code>{' '}
          to change anything here.
        </p>
      </header>

      <Section title="Surfaces">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SwatchTile className="bg-bg-base text-fg" label="bg-base" />
          <SwatchTile className="bg-bg-raised text-fg" label="bg-raised" />
          <SwatchTile className="bg-bg-sunken text-fg" label="bg-sunken" />
          <SwatchTile className="bg-accent text-accent-fg" label="accent" />
        </div>
      </Section>

      <Section title="Foreground ramp">
        <div className="space-y-2 rounded-md bg-bg-raised p-6 shadow-rest">
          <p className="font-display text-3xl text-fg">Atlas of teams</p>
          <p className="text-base text-fg-muted">A muted body line for secondary copy.</p>
          <p className="text-xs uppercase tracking-[0.3em] text-fg-subtle">Subtle eyebrow</p>
        </div>
      </Section>

      <Section title="Program palette">
        <div className="flex flex-wrap gap-2">
          {programLegend.map((item) => (
            <Badge
              key={item.program}
              variant={item.program === 'unknown' ? 'neutral' : item.program}
            >
              <span aria-hidden>{item.glyph}</span>
              {item.label}
            </Badge>
          ))}
        </div>
      </Section>

      <Section title="Type scale">
        <div className="space-y-2">
          <p className="font-display text-6xl">Aa 64</p>
          <p className="font-display text-4xl">Display 48</p>
          <p className="text-2xl">Heading 28</p>
          <p className="text-base">Body 16 — quick brown fox</p>
          <p className="font-mono text-sm">Mono · 839A · 8 0 0 8 X</p>
        </div>
      </Section>

      <Section title="Buttons">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="accent">Take a tour</Button>
          <Button variant="primary">Open team</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Reject</Button>
        </div>
      </Section>

      <Section title="Inputs">
        <div className="max-w-md space-y-3">
          <Input placeholder="Search teams, cities, countries…" />
          <Input placeholder="Invalid example" invalid />
        </div>
      </Section>

      <Section title="Skeleton + state">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 rounded-md bg-bg-raised p-4 shadow-rest">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-3/4" />
          </div>
          <EmptyState
            title="No teams found"
            description="Try widening the search or removing a filter."
          />
        </div>
        <ErrorState description="The atlas hit a snag. Retry to fetch points." retry={() => {}} />
      </Section>

      <Section title="Motion">
        <div className="grid gap-3 sm:grid-cols-3">
          <MotionTile label="fade-in" anim="animate-fade-in" />
          <MotionTile label="fade-up" anim="animate-fade-up" />
          <MotionTile label="pulse-glow" anim="animate-pulse-glow" />
        </div>
      </Section>

      <Section title="Tabs">
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="motion">Motion</TabsTrigger>
            <TabsTrigger value="legend">Legend</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <p className="text-sm text-fg-muted">Surfaces, type, color.</p>
          </TabsContent>
          <TabsContent value="motion">
            <p className="text-sm text-fg-muted">Durations 120/180/240/360 ms.</p>
          </TabsContent>
          <TabsContent value="legend">
            <p className="text-sm text-fg-muted">Glyph + hue per program (a11y).</p>
          </TabsContent>
        </Tabs>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <div className="flex items-end justify-between">
        <h2 className="font-display text-xl tracking-tight">{title}</h2>
        <Separator className="ml-6 flex-1" />
      </div>
      {children}
    </section>
  );
}

function SwatchTile({ className, label }: { className: string; label: string }) {
  return (
    <div
      className={`flex h-24 items-end rounded-md border border-border-default p-3 shadow-rest ${className}`}
    >
      <span className="font-mono text-2xs uppercase tracking-[0.18em]">{label}</span>
    </div>
  );
}

function MotionTile({ label, anim }: { label: string; anim: string }) {
  return (
    <div
      className={`flex h-24 items-center justify-center rounded-md bg-bg-raised text-fg-muted shadow-rest ${anim}`}
    >
      <span className="font-mono text-2xs uppercase tracking-[0.2em]">{label}</span>
    </div>
  );
}
